/* =========================================================================
   LRI HEADER JS
   - Mega menu: hover on desktop, click on mobile, close on outside/Esc.
   - Live predictive search via Shopify's /search/suggest.json endpoint.
   - Boosts part-number (SKU) matches to the top using a custom scorer.
   ========================================================================= */
(function () {
  'use strict';

  const hdr = document.querySelector('[data-lri-header]');
  if (!hdr) return;

  /* -------- MEGA MENU -------- */

  const triggers = hdr.querySelectorAll('[data-lri-mega-trigger]');
  const panels = hdr.querySelectorAll('[data-lri-mega]');
  const isTouch = matchMedia('(hover: none)').matches;
  let openKey = null;
  let hoverTimer = null;

  function openMega(key) {
    panels.forEach(p => {
      const match = p.dataset.lriMega === key;
      p.classList.toggle('is-open', match);
    });
    triggers.forEach(t => {
      const btn = t.querySelector('.lri-hdr__nav-link');
      btn.setAttribute('aria-expanded', t.dataset.lriMegaTrigger === key ? 'true' : 'false');
    });
    openKey = key;
  }

  function closeMega() {
    panels.forEach(p => p.classList.remove('is-open'));
    triggers.forEach(t => {
      t.querySelector('.lri-hdr__nav-link').setAttribute('aria-expanded', 'false');
    });
    openKey = null;
  }

  triggers.forEach(trigger => {
    const btn = trigger.querySelector('.lri-hdr__nav-link');
    const key = trigger.dataset.lriMegaTrigger;

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      openKey === key ? closeMega() : openMega(key);
    });

    if (!isTouch) {
      trigger.addEventListener('mouseenter', () => {
        clearTimeout(hoverTimer);
        openMega(key);
      });
      trigger.addEventListener('mouseleave', () => {
        hoverTimer = setTimeout(() => {
          if (openKey === key && !document.querySelector(`[data-lri-mega="${key}"]:hover`)) {
            closeMega();
          }
        }, 120);
      });
    }
  });

  panels.forEach(p => {
    if (isTouch) return;
    p.addEventListener('mouseenter', () => clearTimeout(hoverTimer));
    p.addEventListener('mouseleave', () => {
      hoverTimer = setTimeout(closeMega, 120);
    });
  });

  // Close on outside click or Esc
  document.addEventListener('click', (e) => {
    if (!hdr.contains(e.target)) closeMega();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { closeMega(); closePsearch(); }
  });

  /* -------- PREDICTIVE SEARCH -------- */

  const searchWrap = hdr.querySelector('[data-lri-search]');
  const input = hdr.querySelector('[data-lri-search-input]');
  const panel = hdr.querySelector('[data-lri-psearch]');
  if (!input || !panel) return;

  const MIN_CHARS = 2;
  const DEBOUNCE = 180;
  let debounceTimer = null;
  let abortCtl = null;
  let activeIndex = -1;
  let items = [];

  // Heuristic: a part-number looks like alphanumeric, >=4 chars, contains a digit.
  const looksLikePartNumber = (q) => /^[A-Za-z0-9\-\/\. ]{4,}$/.test(q) && /\d/.test(q);

  function openPsearch() { panel.hidden = false; panel.classList.add('is-open'); }
  function closePsearch() { panel.classList.remove('is-open'); setTimeout(() => { if (!panel.classList.contains('is-open')) panel.hidden = true; }, 180); activeIndex = -1; }

  function highlight(text, q) {
    if (!text) return '';
    const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return String(text).replace(new RegExp(`(${escaped})`, 'ig'), '<mark>$1</mark>');
  }

  function formatMoney(cents) {
    if (cents == null) return '';
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  // predictive_search.resources.products[].price (and variants[].price) come back
  // from /search/suggest.json as decimal-dollar strings (e.g. "4200.00"), not cents
  // -- unlike normal product/variant price fields elsewhere in the theme.
  function formatSuggestPrice(dollars) {
    if (dollars == null) return '';
    return `$${Number(dollars).toFixed(2)}`;
  }

  // Score a suggestion higher if the query looks like a part number and
  // matches the product SKU/variant title exactly.
  function scoreProduct(p, q) {
    const qLower = q.toLowerCase();
    let score = 0;
    const title = (p.title || '').toLowerCase();
    if (title.startsWith(qLower)) score += 40;
    else if (title.includes(qLower)) score += 20;

    // Variant SKUs — Shopify predictive returns these on variants
    const variants = p.variants || [];
    for (const v of variants) {
      const sku = (v.sku || '').toLowerCase();
      if (!sku) continue;
      if (sku === qLower) score += 100;          // exact SKU = gold
      else if (sku.startsWith(qLower)) score += 60;
      else if (sku.includes(qLower)) score += 30;
    }
    // Bonus for part-number-looking queries hitting any field
    if (looksLikePartNumber(q) && score > 0) score += 15;

    // Tiebreaker: in stock
    if (p.available) score += 2;
    return score;
  }

  async function fetchSuggestions(q) {
    if (abortCtl) abortCtl.abort();
    abortCtl = new AbortController();

    const url = `/search/suggest.json?q=${encodeURIComponent(q)}`
      + `&resources[type]=product,collection,page`
      + `&resources[limit]=8`
      + `&resources[options][unavailable_products]=last`
      + `&resources[options][fields]=title,product_type,variants.title,variants.sku,vendor,body,tag`;

    const res = await fetch(url, { signal: abortCtl.signal, headers: { 'Accept': 'application/json' } });
    if (!res.ok) throw new Error(`Search HTTP ${res.status}`);
    return res.json();
  }

  function renderResults(data, q) {
    const resources = (data && data.resources && data.resources.results) || {};
    const products = resources.products || [];
    const collections = resources.collections || [];
    const pages = resources.pages || [];

    // Re-rank products by our SKU-aware scorer
    const ranked = products
      .map(p => ({ p, s: scoreProduct(p, q) }))
      .sort((a, b) => b.s - a.s)
      .map(x => x.p);

    items = [];
    let html = '';

    if (ranked.length === 0 && collections.length === 0 && pages.length === 0) {
      html = `<div class="lri-psearch__empty">No matches for "<strong>${escapeHtml(q)}</strong>". Try a part number or vehicle model.</div>`;
    } else {
      if (ranked.length) {
        html += `<div class="lri-psearch__group"><div class="lri-psearch__label">Parts</div>`;
        ranked.slice(0, 6).forEach(p => {
          items.push(p.url);
          const primarySku = (p.variants && p.variants[0] && p.variants[0].sku) || '';
          const img = p.image || (p.featured_image && p.featured_image.url) || '';
          const priceDollars = p.price || (p.variants && p.variants[0] && p.variants[0].price) || null;
          html += `
            <a class="lri-psearch__item" href="${p.url}">
              ${img ? `<img class="lri-psearch__thumb" src="${img}&width=120" alt="" loading="lazy">` : `<div class="lri-psearch__thumb"></div>`}
              <div class="lri-psearch__body">
                <div class="lri-psearch__title">${highlight(p.title, q)}</div>
                <div class="lri-psearch__meta">
                  ${primarySku ? `<span class="lri-psearch__sku">${highlight(primarySku, q)}</span>` : ''}
                  ${p.vendor ? `<span>${escapeHtml(p.vendor)}</span>` : ''}
                  <span class="lri-psearch__price">${formatSuggestPrice(priceDollars)}</span>
                </div>
              </div>
            </a>`;
        });
        html += `</div>`;
      }

      if (collections.length) {
        html += `<div class="lri-psearch__group"><div class="lri-psearch__label">Collections</div>`;
        collections.slice(0, 4).forEach(c => {
          items.push(c.url);
          html += `<a class="lri-psearch__item" href="${c.url}"><div class="lri-psearch__thumb"></div><div class="lri-psearch__body"><div class="lri-psearch__title">${highlight(c.title, q)}</div></div></a>`;
        });
        html += `</div>`;
      }

      if (pages.length) {
        html += `<div class="lri-psearch__group"><div class="lri-psearch__label">Pages</div>`;
        pages.slice(0, 3).forEach(pg => {
          items.push(pg.url);
          html += `<a class="lri-psearch__item" href="${pg.url}"><div class="lri-psearch__thumb"></div><div class="lri-psearch__body"><div class="lri-psearch__title">${highlight(pg.title, q)}</div></div></a>`;
        });
        html += `</div>`;
      }

      html += `<a class="lri-psearch__all" href="/search?q=${encodeURIComponent(q)}">See all results for "${escapeHtml(q)}" →</a>`;
    }

    panel.innerHTML = html;
    openPsearch();
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  }

  input.addEventListener('input', () => {
    const q = input.value.trim();
    clearTimeout(debounceTimer);
    if (q.length < MIN_CHARS) {
      closePsearch();
      return;
    }
    debounceTimer = setTimeout(async () => {
      try {
        const data = await fetchSuggestions(q);
        renderResults(data, q);
      } catch (err) {
        if (err.name !== 'AbortError') console.error('[lri-search]', err);
      }
    }, DEBOUNCE);
  });

  input.addEventListener('focus', () => {
    if (input.value.trim().length >= MIN_CHARS && panel.innerHTML) openPsearch();
  });

  document.addEventListener('click', (e) => {
    if (!searchWrap.contains(e.target)) closePsearch();
  });

  // Keyboard nav
  input.addEventListener('keydown', (e) => {
    if (!panel.classList.contains('is-open')) return;
    const anchors = panel.querySelectorAll('.lri-psearch__item');
    if (!anchors.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      activeIndex = (activeIndex + 1) % anchors.length;
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      activeIndex = (activeIndex - 1 + anchors.length) % anchors.length;
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      anchors[activeIndex].click();
      return;
    } else {
      return;
    }
    anchors.forEach((a, i) => a.classList.toggle('is-active', i === activeIndex));
    anchors[activeIndex].scrollIntoView({ block: 'nearest' });
  });
})();
